import type { GridColDef } from '@mui/x-data-grid-premium';
import type { TFunction } from 'i18next';
import type {
  SummaryCardsConfig,
  MetricCardConfig,
} from './email-pulse-summary-cards/email-pulse-summary-cards.types';
import type { EmailPulseMetricDefinition } from './email-pulse-definitions/email-pulse-definitions';
import { EMAIL_PULSE_DEFINITIONS } from './email-pulse-definitions/email-pulse-definitions';
import {
  createEmailPulseDataGridColumns,
  createEmailPulseColumnVisibilityModel,
} from './email-pulse-column-metadata/email-pulse-column-factory';

/**
 * The 6 metric cards shared across all Email Pulse tabs.
 */
export const DEFAULT_METRIC_CARDS: MetricCardConfig[] = [
  {
    dataField: 'emailsSent',
    definition: EMAIL_PULSE_DEFINITIONS.emailsSent,
  },
  {
    dataField: 'emailsDelivered',
    definition: EMAIL_PULSE_DEFINITIONS.emailsDelivered,
  },
  {
    dataField: 'openRate',
    definition: EMAIL_PULSE_DEFINITIONS.openRate,
  },
  {
    dataField: 'clickRate',
    definition: EMAIL_PULSE_DEFINITIONS.clickRate,
  },
  {
    dataField: 'unsubscribeRate',
    definition: EMAIL_PULSE_DEFINITIONS.unsubscribeRate,
  },
  {
    dataField: 'spamReportRate',
    definition: EMAIL_PULSE_DEFINITIONS.spamReportRate,
  },
];

/**
 * Creates a SummaryCardsConfig with standard chart layout and metric cards.
 *
 * @param summaryQueryName - Query name for the main summary data
 * @param testIdPrefix - Prefix for chart test IDs (e.g. 'OverviewSummaryCards')
 * @param metricCards - Optional override for the default 6 metric cards
 */
export const createSummaryCardsConfig = (
  summaryQueryName: string,
  testIdPrefix: string,
  metricCards: MetricCardConfig[] = DEFAULT_METRIC_CARDS,
): SummaryCardsConfig => ({
  summaryQueryName,
  leftChart: {
    queryName: 'emailsOpenedBreakdown',
    definition: EMAIL_PULSE_DEFINITIONS.totalEmailsOpened,
    testId: `${testIdPrefix}.TotalEmailsOpened`,
  },
  rightChart: {
    queryName: 'emailsBouncedBreakdown',
    definition: EMAIL_PULSE_DEFINITIONS.emailsBounced,
    testId: `${testIdPrefix}.EmailsBounced`,
  },
  metricCards,
});

/**
 * Creates data grid configuration (columns + visibility model) for an Email Pulse tab.
 *
 * @param identifierField - The data field name for the first column (e.g. 'messageTitle')
 * @param definition - The metric definition for the identifier column header
 */
export const createDataGridConfig = (
  identifierField: string,
  definition: EmailPulseMetricDefinition,
) => {
  const identifierColumn: GridColDef = {
    field: identifierField,
    headerName: '',
    minWidth: 300,
    hideable: false,
    sortable: true,
    flex: 1,
  };

  return {
    getColumns: (t: TFunction): GridColDef[] => {
      const column: GridColDef = {
        ...identifierColumn,
        headerName: definition.getLabel(t),
      };
      return createEmailPulseDataGridColumns(t, column);
    },
    columnVisibilityModel: createEmailPulseColumnVisibilityModel(),
  };
};
